import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { AuthorFilesFilters } from "@/components/articles/author-files-filters";
import { AuthorFilesTable } from "@/components/articles/author-files-table";
import { authorFileListQueryOptions } from "@/hooks/use-articles";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { AdminAuthorFileItem } from "@/types/article";
import type { PaginationMeta } from "@/types/common";

export default function ArticleFilesPage() {
  const [fileSearch, setFileSearch] = useState("");
  const [unpublishedOnly, setUnpublishedOnly] = useState(false);
  const filePagination = usePagination({ initialLimit: 10 });

  const handleFileSearchChange = (search: string) => {
    setFileSearch(search);
    filePagination.setPage(1);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <nav className="text-muted-foreground mb-2 flex items-center gap-1 text-sm">
          <Link to="/articles" className="hover:text-foreground transition-colors">
            아티클 관리
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">프리미엄 아티클 생성</span>
        </nav>
        <h1 className="text-2xl font-semibold tracking-tight">작가 파일 선택</h1>
        <p className="text-muted-foreground leading-normal">프리미엄 아티클로 발행할 작가 파일을 선택합니다.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>작가 파일</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <div className="flex items-center gap-4">
            <AuthorFilesFilters search={fileSearch} onSearchChange={handleFileSearchChange} />
            <label className="flex shrink-0 cursor-pointer select-none items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={unpublishedOnly}
                onChange={(e) => {
                  setUnpublishedOnly(e.target.checked);
                  filePagination.setPage(1);
                }}
                className="accent-primary h-4 w-4 rounded"
              />
              미발행만 보기
            </label>
          </div>

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback columnCount={4} />}>
                  <SuspenseQuery
                    {...authorFileListQueryOptions(
                      {
                        page: filePagination.page,
                        limit: filePagination.limit,
                      },
                      fileSearch || undefined
                    )}
                  >
                    {({ data }) => (
                      <FilteredAuthorFilesTable
                        data={data.items}
                        meta={data.meta}
                        pagination={filePagination}
                        unpublishedOnly={unpublishedOnly}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>
    </div>
  );
}

interface FilteredAuthorFilesTableProps {
  data: AdminAuthorFileItem[];
  meta: PaginationMeta;
  pagination: ReturnType<typeof usePagination>;
  unpublishedOnly: boolean;
}

function FilteredAuthorFilesTable({ data, meta, pagination, unpublishedOnly }: FilteredAuthorFilesTableProps) {
  const filteredData = useMemo(
    () => (unpublishedOnly ? data.filter((f) => !f.isPublished) : data),
    [data, unpublishedOnly]
  );

  console.log(filteredData);

  if (filteredData.length === 0 && unpublishedOnly) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">현재 페이지에 미발행 파일이 없습니다.</p>
        <Link to="/articles" className="text-primary mt-3 inline-flex items-center gap-1 text-sm hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" />
          아티클 목록으로 돌아가기
        </Link>
      </div>
    );
  }

  return <AuthorFilesTable data={filteredData} meta={meta} pagination={pagination} />;
}
